// react
import React, { useState, useMemo } from "react";

// prop-types
import PropTypes from "prop-types";

// contexts
import { UserAuth } from "context/AuthContext";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TableFooter,
  TablePagination,
  Button,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Tooltip,
} from "@mui/material";
import {
  KeyboardArrowLeftOutlined as KeyboardArrowLeftOutlinedIcon,
  KeyboardArrowRightOutlined as KeyboardArrowRightOutlinedIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";

// utils
import convertTimestamp from "utils/convertTimestamp";

// A ---------------------------------------------------------------------- M

const ActivatedPubKeyTableToolbar = ({ handleOpen }) => {
  const { isReseller } = UserAuth();
  const { t } = useTranslation();

  const buttonStyle = {
    py: 0.5,
    borderColor: "divider",
    color: "text.primary",
    textTransform: "none",
    boxShadow: "none",
    borderRadius: 10,
    "&:hover": {
      borderColor: "primary.main",
      backgroundColor: "action.hover",
    },
  };

  return (
    <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
      <Grid item xs={12} sm={6}>
        <Typography variant="h6" fontWeight="bold">
          {t("activated_public_keys_service_level_2")}
        </Typography>
      </Grid>
      <Grid item container xs={12} sm={6} spacing={1} justifyContent="flex-end">
        {isReseller && (
          <Grid item xs={12} sm={4}>
            <Button variant="outlined" fullWidth startIcon={<AddIcon />} sx={buttonStyle} onClick={handleOpen}>
              {t("public_key")}
            </Button>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

const ActivatedPubKeyTableHead = () => {
  const { t } = useTranslation();

  const headCells = useMemo(
    () => [
      { id: "name", label: t("name") },
      { id: "timestamp", label: t("activation_datetime") },
      { id: "value", label: t("value") },
      { id: "company", label: t("company") },
      { id: "type", label: t("type") },
    ],
    [t]
  );

  return (
    <TableHead sx={{ backgroundColor: "grey.100" }}>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell key={headCell.id} align={headCell.id !== "actions" ? "left" : "right"}>
            <Typography variant="body2" fontWeight="bold">
              {headCell.label}
            </Typography>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const TablePaginationActions = ({ count, page, rowsPerPage, onPageChange }) => {
  const theme = useTheme();
  const isRtl = theme.direction === "rtl";

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={(e) => onPageChange(e, page - 1)} disabled={page === 0} aria-label="previous page">
        {isRtl ? <KeyboardArrowRightOutlinedIcon /> : <KeyboardArrowLeftOutlinedIcon />}
      </IconButton>
      <IconButton onClick={(e) => onPageChange(e, page + 1)} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
        {isRtl ? <KeyboardArrowLeftOutlinedIcon /> : <KeyboardArrowRightOutlinedIcon />}
      </IconButton>
    </Box>
  );
};

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const DataRow = ({ publicKeyData }) => {
  const { t } = useTranslation();

  const [show, setShow] = useState(false);

  const toggleVisibility = () => {
    setShow(!show);
  };

  return (
    <TableRow>
      <TableCell align="left">{publicKeyData.name || "N/A"}</TableCell>
      <TableCell align="left">{publicKeyData.added_on ? convertTimestamp(publicKeyData.added_on) : "N/A"}</TableCell>
      <TableCell align="left" size="small">
        <span style={{ marginRight: "10px" }}>{show ? publicKeyData.publicKey : "••••••"}</span>
        <Tooltip title={show ? t("hide") : t("show")} placement="top">
          <IconButton onClick={toggleVisibility} color="primary">
            {show ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </IconButton>
        </Tooltip>
      </TableCell>
      <TableCell align="left">{publicKeyData.company || "N/A"}</TableCell>
      <TableCell align="left">{publicKeyData.type || "N/A"}</TableCell>
    </TableRow>
  );
};

const ActivatedPubKeyTable = ({ publicKeysData, handleOpen }) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - publicKeysData.length) : 0;

  return (
    <Box>
      <ActivatedPubKeyTableToolbar handleOpen={handleOpen} />
      <TableContainer>
        <Table>
          <ActivatedPubKeyTableHead />
          <TableBody>
            {(rowsPerPage > 0 ? publicKeysData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : publicKeysData).map((publicKeyData) => {
              return <DataRow key={publicKeyData.publicKey} publicKeyData={publicKeyData} />;
            })}
            {emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={5} />
              </TableRow>
            )}
          </TableBody>
          {!isMobile && (
            <TableFooter sx={{ backgroundColor: "grey.100" }}>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[10, 20]}
                  colSpan={5}
                  count={publicKeysData.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage={t("rows_per_page")}
                  labelDisplayedRows={({ from, to, count }) => t("displayed_rows", { from, to, count: count !== -1 ? count : `more than ${to}` })}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          )}
        </Table>
        {isMobile && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <TablePaginationActions count={publicKeysData.length} page={page} rowsPerPage={rowsPerPage} onPageChange={handleChangePage} />
          </Box>
        )}
      </TableContainer>
    </Box>
  );
};

ActivatedPubKeyTable.propTypes = {
  publicKeysData: PropTypes.array.isRequired,
  handleOpen: PropTypes.func.isRequired,
};

export default ActivatedPubKeyTable;
